"use client";

import "./ApprovalCircle.css";

export interface ApprovalCircleProps {
  /** Whether the item is currently approved. */
  approved: boolean;
  /** Called when the button is clicked to toggle approval state. */
  onToggle: () => void;
  /** Tooltip text shown in the pending (unapproved) state. */
  pendingTip?: string;
  /** Tooltip text shown in the approved state. */
  approvedTip?: string;
}

/**
 * Small circular approval indicator. Empty + amber border when unapproved;
 * filled green with an animated checkmark when approved. The single canonical
 * pattern for inline approval state across the app.
 *
 * Hover the component or focus the button to reveal the tooltip.
 */
export function ApprovalCircle({
  approved,
  onToggle,
  pendingTip = "Click to approve.",
  approvedTip = "Approved. Click to unapprove.",
}: ApprovalCircleProps) {
  return (
    <span className="approval-circle-wrap relative inline-flex mr-3">
      <button
        type="button"
        className={[
          "approval-circle-btn",
          "inline-flex items-center justify-center",
          "w-6.5 h-6.5 box-border",
          "bg-(--bg-white)",
          "border-[1.5px] border-[color-mix(in_oklab,var(--status-warning)_55%,transparent)]",
          "rounded-[3px]",
          "text-(--fg-inverse)",
          "cursor-pointer",
          "transition-[background,border-color] duration-120",
          "hover:bg-(--status-warning-bg) hover:border-(--status-warning)",
          approved
            ? "bg-(--status-success)! border-(--status-success)! hover:bg-[color-mix(in_oklab,var(--status-success)_88%,black)]! hover:border-[color-mix(in_oklab,var(--status-success)_88%,black)]!"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={onToggle}
        aria-pressed={approved}
        aria-label={
          approved ? "Approved — click to unapprove" : "Click to approve"
        }
      >
        {approved && (
          <svg
            className="approval-circle-check"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
          >
            <path d="M5 12l5 5L20 7" />
          </svg>
        )}
      </button>
      {/* Tooltip: shown via CSS on wrapper hover / button focus-visible */}
      <span
        className={[
          "approval-circle-tip",
          "absolute bottom-[calc(100%+6px)] right-0 z-30",
          "w-50",
          "px-3 py-2.5",
          "bg-(--bg-white)",
          "border border-(--border-default)",
          "rounded-[3px]",
          "shadow-[0_8px_20px_rgba(10,16,32,0.12),0_2px_6px_rgba(10,16,32,0.06)]",
          "text-[11.5px] leading-normal",
          "text-(--fg-secondary)",
          "hidden pointer-events-none",
        ].join(" ")}
        role="tooltip"
      >
        {approved ? approvedTip : pendingTip}
      </span>
    </span>
  );
}
